/* src/components/customGame/setup/panels/TimingPanel.js */
import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../../ui/Accordion';

// Re-using the SettingsSection helper
const SettingsSection = ({ title, children, helpText = "", className = "" }) => (
    <div className={`mb-6 p-4 border border-gray-200 rounded-md shadow-sm bg-white ${className}`}>
        <h3 className="text-lg font-medium text-gray-800 mb-3">{title}</h3>
        {helpText && <p className="text-sm text-gray-500 mb-3 -mt-2">{helpText}</p>}
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

SettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
    className: PropTypes.string,
};


const TimingPanel = ({ config, onConfigChange, pathPrefix }) => {
    const isiConfig = config?.ISI || {};
    const stimulusDurationConfig = config?.StimulusDuration || {};
    const responseWindowConfig = config?.ResponseWindow || {};
    const fixationConfig = config?.Fixation || {};

    const handleChange = (section, key, value) => {
        // Ensure numeric values are stored as numbers
        let processedValue = value;
        if (typeof value === 'string' && !isNaN(parseFloat(value)) && isFinite(value)) {
             if (key.toLowerCase().includes('duration') || key.toLowerCase().includes('mean') || key.toLowerCase().includes('range') || key.toLowerCase().includes('start') || key.toLowerCase().includes('end')) {
                processedValue = parseInt(value, 10);
            }
        }
        onConfigChange(`${pathPrefix}.${section}.${key}`, processedValue);
    };

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">

            <SettingsSection title="Inter-Stimulus Interval (ISI)" helpText="Time between the end of one stimulus and the start of the next.">
                <div>
                    <label htmlFor="isiMode" className="block text-sm font-medium text-gray-700 mb-1">
                        ISI Mode
                    </label>
                    <select
                        id="isiMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={isiConfig.Mode || 'Fixed'}
                        onChange={(e) => handleChange('ISI', 'Mode', e.target.value)}
                    >
                        <option value="Fixed">Fixed Duration</option>
                        <option value="Variable (Jittered)">Variable Duration (Jittered)</option>
                    </select>
                </div>

                {isiConfig.Mode === 'Fixed' && (
                    <div>
                        <label htmlFor="isiFixedDurationMs" className="block text-sm font-medium text-gray-700 mb-1">
                            Fixed ISI Duration (ms)
                        </label>
                        <input
                            type="number"
                            id="isiFixedDurationMs"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={isiConfig.FixedDurationMs !== undefined ? isiConfig.FixedDurationMs : 2500}
                            onChange={(e) => handleChange('ISI', 'FixedDurationMs', e.target.value)}
                            min="0" max="10000" step="50"
                        />
                    </div>
                )}
                {isiConfig.Mode === 'Variable (Jittered)' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="isiVariableMeanMs" className="block text-sm font-medium text-gray-700 mb-1">
                                Mean ISI Duration (ms)
                            </label>
                            <input
                                type="number"
                                id="isiVariableMeanMs"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={isiConfig.VariableMeanMs !== undefined ? isiConfig.VariableMeanMs : 2500}
                                onChange={(e) => handleChange('ISI', 'VariableMeanMs', e.target.value)}
                                min="500" max="10000" step="50"
                            />
                        </div>
                        <div>
                            <label htmlFor="isiVariableRangeMs" className="block text-sm font-medium text-gray-700 mb-1">
                                ISI Range (+/- ms from mean)
                            </label>
                            <input
                                type="number"
                                id="isiVariableRangeMs"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={isiConfig.VariableRangeMs !== undefined ? isiConfig.VariableRangeMs : 500}
                                onChange={(e) => handleChange('ISI', 'VariableRangeMs', e.target.value)}
                                min="0" max={(isiConfig.VariableMeanMs || 2500) / 2} step="50"
                            />
                        </div>
                    </div>
                )}
            </SettingsSection>

            <SettingsSection title="Stimulus Presentation Duration" helpText="How long each stimulus is visible or audible.">
                 <div>
                    <label htmlFor="stimulusDurationMode" className="block text-sm font-medium text-gray-700 mb-1">
                        Duration Mode
                    </label>
                    <select
                        id="stimulusDurationMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={stimulusDurationConfig.Mode || 'Fixed'}
                        onChange={(e) => handleChange('StimulusDuration', 'Mode', e.target.value)}
                    >
                        <option value="Fixed">Fixed Duration</option>
                        <option value="Variable">Variable Duration</option>
                    </select>
                </div>
                 {stimulusDurationConfig.Mode === 'Fixed' && (
                    <div>
                        <label htmlFor="stimulusFixedDurationMs" className="block text-sm font-medium text-gray-700 mb-1">
                            Fixed Stimulus Duration (ms)
                        </label>
                        <input
                            type="number"
                            id="stimulusFixedDurationMs"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={stimulusDurationConfig.FixedDurationMs !== undefined ? stimulusDurationConfig.FixedDurationMs : 500}
                            onChange={(e) => handleChange('StimulusDuration', 'FixedDurationMs', e.target.value)}
                            min="100" max="5000" step="50"
                        />
                    </div>
                )}
                {stimulusDurationConfig.Mode === 'Variable' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="stimulusVariableMeanMs" className="block text-sm font-medium text-gray-700 mb-1">
                                Mean Stimulus Duration (ms)
                            </label>
                            <input
                                type="number"
                                id="stimulusVariableMeanMs"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={stimulusDurationConfig.VariableMeanMs !== undefined ? stimulusDurationConfig.VariableMeanMs : 500}
                                onChange={(e) => handleChange('StimulusDuration', 'VariableMeanMs', e.target.value)}
                                min="100" max="5000" step="50"
                            />
                        </div>
                        <div>
                            <label htmlFor="stimulusVariableRangeMs" className="block text-sm font-medium text-gray-700 mb-1">
                                Duration Range (+/- ms from mean)
                            </label>
                            <input
                                type="number"
                                id="stimulusVariableRangeMs"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={stimulusDurationConfig.VariableRangeMs !== undefined ? stimulusDurationConfig.VariableRangeMs : 100}
                                onChange={(e) => handleChange('StimulusDuration', 'VariableRangeMs', e.target.value)}
                                min="0" max={(stimulusDurationConfig.VariableMeanMs || 500) / 2} step="10"
                            />
                        </div>
                    </div>
                )}
            </SettingsSection>

            <SettingsSection title="Response Window" helpText="The time window during which user responses are accepted.">
                <div>
                    <label htmlFor="responseWindowMode" className="block text-sm font-medium text-gray-700 mb-1">
                        Response Window Mode
                    </label>
                    <select
                        id="responseWindowMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={responseWindowConfig.Mode || 'Entire Trial'}
                        onChange={(e) => handleChange('ResponseWindow', 'Mode', e.target.value)}
                    >
                        <option value="Entire Trial">Entire Trial (Stimulus Onset to Next Stimulus Onset)</option>
                        <option value="During ISI">Only During Inter-Stimulus Interval (ISI)</option>
                        <option value="During Stimulus">Only During Stimulus Presentation</option>
                        <option value="Custom Window">Custom Window (Relative to Stimulus Onset)</option>
                    </select>
                </div>
                {responseWindowConfig.Mode === 'Custom Window' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="responseWindowCustomStartMs" className="block text-sm font-medium text-gray-700 mb-1">
                                Window Start (ms from stimulus onset)
                            </label>
                            <input
                                type="number"
                                id="responseWindowCustomStartMs"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={responseWindowConfig.CustomStartMs !== undefined ? responseWindowConfig.CustomStartMs : 0}
                                onChange={(e) => handleChange('ResponseWindow', 'CustomStartMs', e.target.value)}
                                min="0" step="50"
                            />
                        </div>
                        <div>
                            <label htmlFor="responseWindowCustomEndMs" className="block text-sm font-medium text-gray-700 mb-1">
                                Window End (ms from stimulus onset)
                            </label>
                            <input
                                type="number"
                                id="responseWindowCustomEndMs"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={responseWindowConfig.CustomEndMs !== undefined ? responseWindowConfig.CustomEndMs : 3000}
                                onChange={(e) => handleChange('ResponseWindow', 'CustomEndMs', e.target.value)}
                                min="100" step="50" // Must be at least stimulus duration
                            />
                        </div>
                    </div>
                )}
                 <p className="text-xs text-gray-500 mt-1">
                    Total trial time (stimulus + ISI) for reference:
                    {(stimulusDurationConfig.Mode === 'Fixed' ? (stimulusDurationConfig.FixedDurationMs || 0) : (stimulusDurationConfig.VariableMeanMs || 0)) +
                     (isiConfig.Mode === 'Fixed' ? (isiConfig.FixedDurationMs || 0) : (isiConfig.VariableMeanMs || 0))} ms.
                </p>
            </SettingsSection>

            <SettingsSection title="Fixation Point / Inter-Trial Visuals">
                <div className="flex items-center">
                    <input
                        id="fixationEnabled"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        checked={fixationConfig.Enabled !== undefined ? fixationConfig.Enabled : true}
                        onChange={(e) => handleChange('Fixation', 'Enabled', e.target.checked)}
                    />
                    <label htmlFor="fixationEnabled" className="ml-2 block text-sm font-medium text-gray-700">
                        Show Fixation Point (during ISI)
                    </label>
                </div>
                {fixationConfig.Enabled && (
                    <div className="pl-2 mt-3 space-y-3 border-l-2 border-gray-200 ml-2">
                        <div>
                            <label htmlFor="fixationAppearance" className="block text-sm font-medium text-gray-700 mb-1">
                                Fixation Appearance
                            </label>
                            <select
                                id="fixationAppearance"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                value={fixationConfig.Appearance || 'Crosshair (+)'}
                                onChange={(e) => handleChange('Fixation', 'Appearance', e.target.value)}
                            >
                                <option value="Crosshair (+)">Crosshair (+)</option>
                                <option value="Dot">Dot (•)</option>
                                {/* <option value="Custom Character/Image">Custom Character/Image</option> */}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="fixationColor" className="block text-sm font-medium text-gray-700 mb-1">
                                Fixation Color
                            </label>
                            <input
                                type="color"
                                id="fixationColor"
                                className="mt-1 block w-full h-10 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                value={fixationConfig.Color || '#374151'} // Default dark gray
                                onChange={(e) => handleChange('Fixation', 'Color', e.target.value)}
                            />
                        </div>
                        {/* Fixation Duration is usually tied to ISI, but could be configurable */}
                        {/* For simplicity, if needed, it defaults to ISI duration */}
                         <p className="text-xs text-gray-500">
                            Fixation point duration typically matches the ISI duration.
                            Current ISI (fixed or mean): {isiConfig.Mode === 'Fixed' ? (isiConfig.FixedDurationMs || 0) : (isiConfig.VariableMeanMs || 0)} ms.
                        </p>
                    </div>
                )}
            </SettingsSection>

        </div>
    );
};

TimingPanel.propTypes = {
    config: PropTypes.shape({
        ISI: PropTypes.object,
        StimulusDuration: PropTypes.object,
        ResponseWindow: PropTypes.object,
        Fixation: PropTypes.object,
    }).isRequired,
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Timing"
};

export default TimingPanel;